import { Component, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

type NoteLevel = 1 | 2 | 3 | 4 | 5;
type AvisCategorie = 'attente' | 'accueil' | 'prelevement' | 'resultats';

interface Avis {
  id: number;
  note: NoteLevel;
  commentaire: string;
  date: string;
  site: string;
  categorie: AvisCategorie;
}

@Component({
  selector: 'app-avis-patient-screen',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './avis-patient-screen.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./avis-patient-screen.component.scss'],
})
export class AvisPatientScreenComponent {
  readonly selectedAvis = signal<Avis | null>(null);
  readonly selectedSite = signal<string | null>(null);

  readonly sites = ['Nantes Centre', 'Carquefou', 'Saint-Herblain', 'Rezé', 'Orvault', 'Vertou'];

  readonly filteredAvis = computed(() => {
    const s = this.selectedSite();
    return s ? this.avis.filter(a => a.site === s) : this.avis;
  });

  readonly avis: Avis[] = [
    { id: 1, note: 5, commentaire: 'Accueil très chaleureux, prélèvement rapide et indolore. Personnel attentionné et professionnel. Je recommande vivement.', date: "Aujourd'hui · 09:14", site: 'Nantes Centre', categorie: 'accueil' },
    { id: 2, note: 2, commentaire: "Attente de plus de 45 minutes sans aucune information. Personnel peu disponible à l'accueil. Décevant.", date: "Aujourd'hui · 08:47", site: 'Nantes Centre', categorie: 'attente' },
    { id: 3, note: 4, commentaire: 'Bon accueil, prélèvement efficace. Légère attente mais tout s\'est très bien passé dans l\'ensemble.', date: 'Hier · 16:30', site: 'Carquefou', categorie: 'prelevement' },
    { id: 4, note: 1, commentaire: 'Résultats reçus avec 2 jours de retard, sans aucune communication préalable. Très décevant, cela a retardé mon traitement.', date: 'Hier · 11:05', site: 'Saint-Herblain', categorie: 'resultats' },
    { id: 5, note: 5, commentaire: 'Parfait de bout en bout. Personnel souriant et efficace, résultats disponibles en moins de 2h. Bravo.', date: 'Hier · 09:22', site: 'Rezé', categorie: 'accueil' },
    { id: 6, note: 3, commentaire: 'Service correct mais attente longue. Le préleveur était agréable et rassurant.', date: 'Il y a 2j · 14:15', site: 'Nantes Centre', categorie: 'attente' },
    { id: 7, note: 4, commentaire: 'Très bonne expérience, personnel souriant. Petit bémol sur le stationnement difficile à trouver.', date: 'Il y a 2j · 10:33', site: 'Orvault', categorie: 'accueil' },
    { id: 8, note: 2, commentaire: 'Difficile d\'accéder aux résultats en ligne, le portail ne fonctionnait pas correctement depuis 3 jours.', date: 'Il y a 3j · 08:50', site: 'Vertou', categorie: 'resultats' },
  ];

  filledStars(n: NoteLevel): number[] { return Array.from({ length: n }); }
  emptyStars(n: NoteLevel): number[] { return Array.from({ length: 5 - n }); }

  noteColor(n: NoteLevel): string {
    if (n >= 4) return '#059669';
    if (n === 3) return '#D97706';
    return '#DC2626';
  }

  noteBg(n: NoteLevel): string {
    if (n >= 4) return '#D1FAE5';
    if (n === 3) return '#FEF3C7';
    return '#FEE2E2';
  }

  noteLabel(n: NoteLevel): string {
    return ({ 1: 'Très mauvais', 2: 'Mauvais', 3: 'Moyen', 4: 'Bien', 5: 'Excellent' })[n];
  }

  categorieLabel(c: AvisCategorie): string {
    return ({ attente: 'Attente', accueil: 'Accueil', prelevement: 'Prélèvement', resultats: 'Résultats' })[c];
  }

  private catDragX = 0;
  private catScrollStart = 0;
  private catDragging = false;
  private catDelta = 0;

  onCatDown(e: MouseEvent, el: HTMLElement) {
    this.catDragging = true;
    this.catDelta = 0;
    this.catDragX = e.clientX;
    this.catScrollStart = el.scrollLeft;
  }
  onCatMove(e: MouseEvent, el: HTMLElement) {
    if (!this.catDragging) return;
    this.catDelta = e.clientX - this.catDragX;
    el.scrollLeft = this.catScrollStart - this.catDelta;
  }
  onCatUp() { this.catDragging = false; }

  selectSite(site: string | null) {
    if (Math.abs(this.catDelta) > 5) return;
    if (site === null) { this.selectedSite.set(null); return; }
    this.selectedSite.set(this.selectedSite() === site ? null : site);
  }

  select(a: Avis) { this.selectedAvis.set(a); }
  backToList() { this.selectedAvis.set(null); }

  moyenneNote(): string {
    const avg = this.avis.reduce((s, a) => s + a.note, 0) / this.avis.length;
    return avg.toFixed(1);
  }

  repartition(n: number): number {
    return Math.round(this.avis.filter(a => a.note === n).length / this.avis.length * 100);
  }
}